/**
 * Pixa sayfalarının ortak parçaları.
 *
 * 17 sayfa tek dosyada tutulamayacağı için ortak kancalar ve bileşenler burada,
 * sayfalar src/pixa/{Sales,Catalog,Stock,System,Asistan}.tsx içinde.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { T, FONT, MONO, radius, tint, alpha } from '../poyais-ui/theme';
import { Card, Grid, Eyebrow, Badge, Button } from '../poyais-ui/primitives';
import { Icon } from '../poyais-ui/icons';
import { exportCsv } from '../poyais-ui/export';
import { useLang, addTranslations } from '../i18n';
import { titleCase } from './reportLabels';
import PoyaisSpinner from '../PoyaisSpinner';

export const API = '/api/v1';
export const PAGE_SIZE = 50;
export const CONFIRM_WORD = 'ONAYLA';

addTranslations({
  'Pixa Bağlı Değil': 'Pixa Not Connected',
  'Ayarlar sayfasından Pixa hesabınızı bağlayın.': 'Connect your Pixa account from the Settings page.',
  'Ayarlara Git': 'Go to Settings',
  'Veri alınamadı': 'Could not load data', 'Hata': 'Error',
  'Kayıt bulunamadı.': 'No records found.', 'Bu Sayfayı Dışa Aktar': 'Export this page',
  'kayıt': 'records', 'Önceki': 'Previous', 'Sonraki': 'Next', 'Tümü': 'All',
  'Filtreleri Temizle': 'Clear filters', 'Ara…': 'Search…', 'Yükleniyor…': 'Loading…',
  'Vazgeç': 'Cancel', 'İşleniyor…': 'Processing…', 'İşlem tamamlandı.': 'Done.',
  'Bu işlem geri alınamaz.': 'This action cannot be undone.',
  'Onaylamak için ONAYLA yazın': 'Type ONAYLA to confirm',
  'Onayla ve Uygula': 'Confirm and apply',
  'Etkilenecek kayıtlar': 'Records affected',
  've': 'and', 'daha': 'more',
});

export function fmtN(v: any) { return Number(v || 0).toLocaleString('tr-TR'); }

/** Büyük harf, panelin diline göre: Türkçe'de i→İ, İngilizce'de i→I ("OPPORTUNITY"). */
export const upperUi = (s: string) => {
  let en = false;
  try { en = localStorage.getItem('poyais_lang') === 'en'; } catch { }
  return en ? s.toUpperCase() : s.toLocaleUpperCase('tr');
};

export function money(v: any, cur = 'TRY') {
  const n = Number(v || 0);
  try { return n.toLocaleString('tr-TR', { style: 'currency', currency: cur || 'TRY', maximumFractionDigits: 0 }); }
  catch { return fmtN(n); }
}

export function fmtDate(v: any) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function fmtDateTime(v: any) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v)
    : d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function timeAgo(v: any) {
  if (!v) return '—';
  const d = new Date(String(v).includes('T') ? v : String(v).replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return String(v);
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (mins < 1) return 'az önce';
  if (mins < 60) return `${mins} dk önce`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} saat önce`;
  return `${Math.round(h / 24)} gün önce`;
}

export function Loader() { return <div className="py-pageload"><PoyaisSpinner size={38} pad={0}/></div>; }

export function DisconnectedState() {
  const { t } = useLang();
  return (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 320, gap: 14, fontFamily: FONT }}>
    <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{t('Pixa Bağlı Değil')}</div>
    <div style={{ fontSize: 13, color: T.textDim }}>{t('Ayarlar sayfasından Pixa hesabınızı bağlayın.')}</div>
    <Button variant="solid" color={T.accent} onClick={() => window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'settings' }))}>{t('Ayarlara Git')}</Button>
  </div>);
}

export function ErrorBox({ msg }: { msg: string }) {
  const { t } = useLang();
  return (<div style={{ background: tint(T.red, 8), border: `1px solid ${alpha(T.red, 25)}`, borderRadius: radius.lg, padding: '20px 24px', fontFamily: FONT }}>
    <div style={{ fontSize: 13.5, fontWeight: 800, color: T.redText, marginBottom: 6 }}>{t('Hata')}</div>
    <div style={{ fontSize: 12, color: T.textMid }}>{msg}</div>
  </div>);
}

/** Liste kancası. Parametreler stabil bir anahtara çevrilip gereksiz refetch engellenir —
 *  Pixa dakikada 100 istek kabul ediyor, backend ayrıca 30sn cache tutuyor. */
export function useList(clientId: number, path: string, params: Record<string, any>) {
  const key = JSON.stringify(params);
  const [state, setState] = useState<any>({ rows: [], pagination: {}, loading: true, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setState((s: any) => ({ ...s, loading: true, error: null }));
    axios.get(`${API}/pixa/${clientId}${path}`, { params: JSON.parse(key) })
      .then(r => { if (alive) setState({ rows: r.data?.rows || [], pagination: r.data?.pagination || {}, loading: false, error: null }); })
      .catch(e => { if (alive) setState({ rows: [], pagination: {}, loading: false, error: e?.response?.data?.detail || 'Veri alınamadı' }); });
    return () => { alive = false; };
  }, [clientId, path, key, tick]);

  return { ...state, reload: useCallback(() => setTick(x => x + 1), []) };
}

/** Tek nesne dönen uçlar için (raporlar, teşhis, analiz). */
export function useData(clientId: number, path: string, params: Record<string, any> = {}, skip = false) {
  const key = JSON.stringify(params);
  const [state, setState] = useState<any>({ data: null, loading: !skip, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (skip) { setState({ data: null, loading: false, error: null }); return; }
    let alive = true;
    setState((s: any) => ({ ...s, loading: true, error: null }));
    axios.get(`${API}/pixa/${clientId}${path}`, { params: JSON.parse(key) })
      .then(r => { if (alive) setState({ data: r.data, loading: false, error: null }); })
      .catch(e => { if (alive) setState({ data: null, loading: false, error: e?.response?.data?.detail || 'Veri alınamadı' }); });
    return () => { alive = false; };
  }, [clientId, path, key, tick, skip]);

  return { ...state, reload: useCallback(() => setTick(x => x + 1), []) };
}

export function useEnums(clientId: number) {
  const [tables, setTables] = useState<any>({});
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/pixa/${clientId}/enums`).then(r => { if (alive) setTables(r.data?.tables || {}); }).catch(() => { });
    return () => { alive = false; };
  }, [clientId]);
  return tables;
}

/** Entegrasyon listesi — dropdown'lar ve aktarma konsolu için.
 *  onlyMarketplaces: ERP ve kargo entegrasyonlarını eler (Nebim bir entegratör,
 *  satış kanalı değil; "ürün gönder" gibi yerlerde görünmemeli). */
export function useIntegrations(clientId: number, onlyMarketplaces = true) {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/pixa/${clientId}/integrations`)
      .then(r => {
        if (!alive) return;
        const all = r.data?.rows || [];
        setRows(onlyMarketplaces ? all.filter((i: any) => i.isMarketplace !== false) : all);
      }).catch(() => { });
    return () => { alive = false; };
  }, [clientId, onlyMarketplaces]);
  return rows;
}

export const inputStyle: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 8, border: `1px solid ${T.borderMid}`, fontSize: 12,
  fontFamily: FONT, background: T.subtleBg, outline: 'none', color: T.text, boxSizing: 'border-box',
};

export function FilterBar({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, alignItems: 'center' }}>{children}</div>;
}

export function EnumSelect({ label, table, value, onChange }: { label: string; table: any; value: string; onChange: (v: string) => void }) {
  const { t } = useLang();
  const entries = useMemo(() => Object.entries(table || {}).sort((a: any, b: any) => Number(a[0]) - Number(b[0])), [table]);
  if (!entries.length) return null;
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, minWidth: 130 }}>
      <option value="">{t(label)}: {t('Tümü')}</option>
      {entries.map(([val, lbl]: any) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  );
}

export function Pager({ pagination, offset, onOffset }: { pagination: any; offset: number; onOffset: (o: number) => void }) {
  const { t } = useLang();
  const total = pagination?.total ?? 0;
  const limit = pagination?.limit ?? PAGE_SIZE;
  if (!total) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 20px', borderTop: `1px solid ${T.border}`, flexWrap: 'wrap' as const }}>
      <div style={{ fontSize: 11, color: T.textDim, fontVariantNumeric: 'tabular-nums' }}>
        {offset + 1}–{Math.min(offset + limit, total)} / {fmtN(total)} {t('kayıt')}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <Button variant="ghost" color={T.accent} disabled={offset <= 0} onClick={() => onOffset(Math.max(0, offset - limit))} style={{ padding: '5px 12px', fontSize: 11 }}>← {t('Önceki')}</Button>
        <Button variant="ghost" color={T.accent} disabled={!pagination?.hasMore} onClick={() => onOffset(offset + limit)} style={{ padding: '5px 12px', fontSize: 11 }}>{t('Sonraki')} →</Button>
      </div>
    </div>
  );
}

export type Col = { key: string; label: string; align?: 'left' | 'right' | 'center'; render?: (row: any) => React.ReactNode };

export function DataTable({ title, subtitle, empty, cols, rows, loading, error, pagination, offset, onOffset, csvName, minWidth = 700, toolbar, selectable, selected, onToggle, onToggleAll }: {
  title: string; subtitle?: React.ReactNode; empty?: React.ReactNode; cols: Col[]; rows: any[]; loading?: boolean; error?: string | null;
  pagination?: any; offset?: number; onOffset?: (o: number) => void; csvName?: string; minWidth?: number;
  toolbar?: React.ReactNode; selectable?: boolean; selected?: Set<number>; onToggle?: (id: number) => void; onToggleAll?: () => void;
}) {
  const { t } = useLang();
  const allSel = selectable && rows.length > 0 && rows.every((r: any) => selected?.has(r.id));
  return (
    <Card pad={0}>
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{titleCase(title)}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: T.textDim, marginTop: 3, lineHeight: 1.5 }}>{subtitle}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const }}>
          {toolbar}
          {csvName && (
            <Button variant="ghost" color={T.accent} disabled={!rows.length}
              onClick={() => exportCsv(csvName, rows, cols.filter(c => c.key).map(c => ({ key: c.key, label: c.label })))}
              style={{ padding: '6px 12px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon.download size={13} />{t('Bu Sayfayı Dışa Aktar')}
            </Button>
          )}
        </div>
      </div>
      {error ? <div style={{ padding: 20 }}><ErrorBox msg={error} /></div> : loading ? <Loader /> : (
        <div style={{ overflowX: 'auto' as const }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' as const, minWidth }}>
            <thead style={{ borderBottom: `1px solid ${T.border}`, background: T.card }}>
              <tr>
                {selectable && (
                  <th style={{ width: 34, padding: '10px 0 10px 14px' }}>
                    <input type="checkbox" checked={!!allSel} onChange={() => onToggleAll?.()} style={{ cursor: 'pointer' }} />
                  </th>
                )}
                {cols.map((c, i) => (
                  <th key={c.key} style={{ padding: '10px 14px', fontSize: 9, fontWeight: 700, color: T.textDim, textTransform: 'uppercase' as const, letterSpacing: '0.08em', textAlign: c.align || (i === 0 ? 'left' : 'right'), whiteSpace: 'nowrap' as const }}>{t(c.label)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={cols.length + (selectable ? 1 : 0)} style={{ padding: '28px 14px', textAlign: 'center' as const, fontSize: 12.5, color: T.textDim, lineHeight: 1.6 }}>{empty || t('Kayıt bulunamadı.')}</td></tr>
              ) : rows.map((r: any, ri: number) => (
                <tr key={r.id ?? ri} style={{ borderBottom: `1px solid ${T.card}` }}
                  onMouseEnter={e => (e.currentTarget.style.background = T.card)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  {selectable && (
                    <td style={{ padding: '10px 0 10px 14px' }}>
                      <input type="checkbox" checked={!!selected?.has(r.id)} onChange={() => onToggle?.(r.id)} style={{ cursor: 'pointer' }} />
                    </td>
                  )}
                  {cols.map((c, i) => (
                    <td key={c.key} style={{ padding: '11px 14px', fontSize: 12, color: i === 0 ? T.text : T.textMid, textAlign: c.align || (i === 0 ? 'left' : 'right'), fontVariantNumeric: 'tabular-nums', whiteSpace: c.render ? undefined : 'nowrap' as const }}>
                      {c.render ? c.render(r) : (r[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pagination && onOffset && <Pager pagination={pagination} offset={offset || 0} onOffset={onOffset} />}
    </Card>
  );
}

/** Sayfa içi sekme — WMS/Kayıtlar/Raporlar/Fiyatlandırma birden çok ucu tek sayfada barındırıyor. */
export function Tabs({ tabs, value, onChange }: { tabs: { id: string; label: string }[]; value: string; onChange: (v: string) => void }) {
  const { t } = useLang();
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, borderBottom: `1px solid ${T.border}`, paddingBottom: 10 }}>
      {tabs.map(tb => (
        <Button key={tb.id} variant={value === tb.id ? 'soft' : 'ghost'} color={T.accent} onClick={() => onChange(tb.id)}
          style={{ padding: '6px 14px', fontSize: 11.5, fontWeight: value === tb.id ? 700 : 500 }}>
          {titleCase(t(tb.label))}
        </Button>
      ))}
    </div>
  );
}

/** Ürün küçük resmi. thumbUrl backend'de pick_image ile seçiliyor; bozuk/403 linkte
 *  gri kutuya düşer (CDN linkleri erişilemez olabiliyor). */
export function Thumb({ url, size = 28 }: { url?: string | null; size?: number }) {
  const [bad, setBad] = useState(false);
  if (!url || bad) return <div style={{ width: size, height: size, borderRadius: 6, background: T.card, flexShrink: 0 }} />;
  return <img src={url} alt="" width={size} height={size} loading="lazy" decoding="async"
    referrerPolicy="no-referrer" onError={() => setBad(true)}
    style={{ borderRadius: 6, objectFit: 'cover' as const, flexShrink: 0, background: T.card }} />;
}

/**
 * Geri alınamaz işlemlerin onay modalı.
 *
 * Kullanıcı ONAYLA yazmadan buton açılmaz — ama bu yalnızca UX kapısı. Gerçek kapı
 * sunucudaki confirm_or_400() + require_client_admin; istemciye güvenilmiyor.
 */
export function ConfirmDangerModal({ open, title, description, items, confirmLabel, busy, error, onCancel, onConfirm }: {
  open: boolean; title: string; description: string; items?: string[];
  confirmLabel?: string; busy?: boolean; error?: string | null;
  onCancel: () => void; onConfirm: () => void;
}) {
  const { t } = useLang();
  const [word, setWord] = useState('');
  useEffect(() => { if (open) setWord(''); }, [open]);
  if (!open) return null;
  const ok = word.trim().toUpperCase() === CONFIRM_WORD;
  return (
    <div onClick={() => !busy && onCancel()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, borderRadius: 14, border: `1px solid ${T.border}`, padding: '24px 26px', width: '100%', maxWidth: 460, fontFamily: FONT, maxHeight: '86vh', overflowY: 'auto' as const }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: T.text, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.7, marginBottom: 14 }}>{description}</div>
        {items && items.length > 0 && (
          <div style={{ background: T.card, borderRadius: 8, padding: '10px 12px', marginBottom: 14, maxHeight: 160, overflowY: 'auto' as const }}>
            <Eyebrow color={T.textDim}>{t('Etkilenecek kayıtlar')}</Eyebrow>
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {items.slice(0, 12).map((s, i) => (
                <div key={i} style={{ fontSize: 11.5, color: T.textMid, fontFamily: MONO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{s}</div>
              ))}
              {items.length > 12 && <div style={{ fontSize: 11, color: T.textDim }}>… {t('ve')} {items.length - 12} {t('daha')}</div>}
            </div>
          </div>
        )}
        <div style={{ fontSize: 11.5, color: T.redText, background: tint(T.red, 8), padding: '9px 11px', borderRadius: 8, marginBottom: 12, fontWeight: 600 }}>
          {t('Bu işlem geri alınamaz.')}
        </div>
        <div style={{ fontSize: 9, fontWeight: 700, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase' as const, marginBottom: 6 }}>
          {t('Onaylamak için ONAYLA yazın')}
        </div>
        <input value={word} onChange={e => setWord(e.target.value)} placeholder={CONFIRM_WORD} autoFocus
          style={{ ...inputStyle, width: '100%', fontFamily: MONO, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 12 }} />
        {error && <div style={{ fontSize: 11.5, color: T.redText, marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" color={T.textDim} disabled={busy} onClick={onCancel} style={{ flex: 1, padding: '9px 12px', fontSize: 12 }}>{t('Vazgeç')}</Button>
          <Button variant="solid" color={T.red} disabled={!ok || busy} onClick={onConfirm}
            style={{ flex: 1.4, padding: '9px 12px', fontSize: 12, opacity: ok && !busy ? 1 : 0.45 }}>
            {busy ? t('İşleniyor…') : (confirmLabel || t('Onayla ve Uygula'))}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Kısa bildirim çubuğu (aksiyon sonuçları). */
export function Toast({ msg, tone = 'ok' }: { msg: string; tone?: 'ok' | 'err' }) {
  if (!msg) return null;
  const c = tone === 'err' ? T.redText : T.greenText;
  return <div style={{ fontSize: 11.5, color: c, fontWeight: 600 }}>{msg}</div>;
}

export { titleCase };
export { T, FONT, MONO, radius, tint, alpha, Card, Grid, Eyebrow, Badge, Button, Icon, exportCsv, useLang, axios };
